import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiClock, FiCalendar, FiMessageSquare, FiGlobe, FiFlag, FiThumbsUp, FiEye, FiLock } from 'react-icons/fi';
import { FaDiscord, FaThumbsUp } from 'react-icons/fa';
import { BsFillStarFill } from 'react-icons/bs';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import bannerGif from '../../../../assets/images/banner.gif';

import { HomeWrapper } from '../home/HomeStyle';
import { getServers, voteServer, viewServer } from '../../../../store/servers/serversSlice';
import { getComments, createComment, likeComment } from '../../../../store/comments/commentsSlice';
import { COUNTRIES } from '../../../../data/countries';

import {
  ServerHero,
  StatsBar,
  DetailsLayout,
  DescriptionBox,
  DetailsSidebar,
  CommentsSection,
} from './ServerDetailsStyle';
import { backendUrl } from "../../../../utilities/axios";

const StarRow = ({ value, onChange }) => (
  <div className="star-row">
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} className={`star ${n <= value ? 'filled' : ''}`} onClick={() => onChange(n)}>★</span>
    ))}
  </div>
);

const CommentStars = ({ count }) => (
  <div className="comment-stars">
    {[1, 2, 3, 4, 5].map(n => (
      <BsFillStarFill key={n} color={n <= count ? '#ffd700' : '#444'} />
    ))}
  </div>
);

const getCommentBadge = (createdAt, t) => {
  if (!createdAt) return null;
  const diffMs   = Date.now() - new Date(createdAt).getTime();
  const diffHours = diffMs / 3_600_000;
  const diffDays  = diffMs / 86_400_000;
  if (diffHours < 24) return { label: t('website.server_details.comment_badge_new'),  cls: 'cbadge-new' };
  if (diffDays  < 1)  return { label: t('website.server_details.comment_badge_today'), cls: 'cbadge-today' };
  if (diffDays  < 7)  return { label: t('website.server_details.comment_badge_week'),  cls: 'cbadge-week' };
  return null;
};

const ServerDetails = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const { data: serversData } = useSelector(state => state.servers);
  const { data: commentsData, isLoading: commentsLoading } = useSelector(state => state.comments);
  const auth = useSelector(state => state.auth);

  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [commentPage, setCommentPage] = useState(1);
  const [optimisticVote, setOptimisticVote] = useState(false);

  const server = serversData?.data?.find(s => s._id === id);
  const userId = auth.user?._id;
  const isVoted = optimisticVote || !!server?.likedBy?.some(lid => lid === userId);

  useEffect(() => {
    dispatch(getServers({ page: 1, size: 50, query: '', filter: JSON.stringify({}) }));
    dispatch(getComments({ page: 1, size: 10, serverId: id }));
    dispatch(viewServer(id));
  }, [dispatch, id]);
  const comments = commentsData?.data || [];
  const totalComments = commentsData?.itemsCount || 0;

  const getImage = (srv) => srv?.image ? `${backendUrl}/servers/${srv.image}` : bannerGif;

  // Show bilingual name / description based on current language
  const displayName = server
    ? (i18n.language === 'ar'
      ? (server.name_ar || server.name)
      : (server.name_en || server.name))
    : '';

  const displayDesc = server
    ? (i18n.language === 'ar'
      ? (server.description_ar || server.description)
      : (server.description_en || server.description))
    : '';

  const handleVote = () => {
    if (!auth.user || isVoted || !server) return;
    setOptimisticVote(true);
    dispatch(voteServer(server)).unwrap().catch(err => {
      if (err?.errors?.[0]?.msg === 'already_liked') return;
      setOptimisticVote(false);
    });
  };

  const handleSubmitReview = () => {
    if (!reviewText.trim() || !auth.isAuth || !server) return;
    dispatch(createComment({
      server: server._id,
      author: auth.user.name,
      country: auth.user.country || null,
      text: reviewText,
      stars: rating,
    })).unwrap().then(() => {
      setReviewText('');
      setRating(5);
    });
  };

  if (!server) {
    return (
      <WebsiteLayout>
        <div style={{ padding: '60px', color: '#fff', textAlign: 'center' }}>
          {t('website.server_details.loading')}
        </div>
      </WebsiteLayout>
    );
  }

  return (
    <HomeWrapper>
      <WebsiteLayout>
        <ServerHero>
          <div className="hero-inner">
            <h1 className="server-name">{displayName}</h1>
            <p className="server-tagline">{t('website.server_details.max_level')}: {server.level}</p>
            <img className="server-banner-img" src={getImage(server)} alt={displayName} />
          </div>
        </ServerHero>

        <StatsBar>
          <div className="stats-row">
            <div className="stat-item">
              <FiClock className="stat-icon" />
              <div className="stat-body">
                <div className="stat-label">{t('website.server_details.max_level')}</div>
                <div className="stat-value">{server.level}</div>
              </div>
            </div>
            {server.launch_date && (
              <div className="stat-item">
                <FiCalendar className="stat-icon" />
                <div className="stat-body">
                  <div className="stat-label">{t('website.server_details.launch_date')}</div>
                  <div className="stat-value">{new Date(server.launch_date).toLocaleDateString()}</div>
                </div>
              </div>
            )}
            <div className="stat-item">
              <FiMessageSquare className="stat-icon" />
              <div className="stat-body">
                <div className="stat-label">{t('website.server_details.comments_label')}</div>
                <div className="stat-value">{totalComments}</div>
              </div>
            </div>
            <div className="stat-item">
              <FiEye className="stat-icon" />
              <div className="stat-body">
                <div className="stat-label">{t('website.server_details.views')}</div>
                <div className="stat-value" style={{ direction: 'ltr' }}>{(server.views || 0).toLocaleString()}</div>
              </div>
            </div>
            {server.languages?.length > 0 && (
              <div className="stat-item">
                <FiGlobe className="stat-icon" />
                <div className="stat-body">
                  <div className="stat-label">{t('website.server_details.languages_label')}</div>
                  <div className="stat-value">
                    {server.languages.length}
                    <span className="lang-flags">{server.languages.map((f, i) => <span key={i}>{f}</span>)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </StatsBar>

        <DetailsLayout>
          <DescriptionBox>
            {displayDesc
              ? <p>{displayDesc}</p>
              : <p style={{ color: '#6b7280' }}>{t('website.server_details.no_description')}</p>
            }
          </DescriptionBox>

          <DetailsSidebar className="details-sidebar">
            <div className="rank-card">
              <div className="rank-label">{t('website.server_details.votes')}</div>
              <div className="rank-number">{(server.votes || 0).toLocaleString()}</div>
            </div>

            <div
              className={`vote-card${isVoted ? ' voted' : ''}${!auth.user ? ' locked' : ''}`}
              onClick={handleVote}
              title={!auth.user ? 'يجب تسجيل الدخول للإعجاب' : isVoted ? 'أعجبك هذا السيرفر' : 'أعجبني'}
            >
              {!auth.user ? <FiLock className="vote-icon" /> : <FaThumbsUp className="vote-icon" />}
              <span className="vote-count">{server.votes.toLocaleString()}</span>
            </div>

            {server.website_url && (
              <a href={server.website_url} className="action-btn website-btn" target="_blank" rel="noreferrer">
                <FiGlobe size={14} /> {t('website.server_details.website_btn')}
              </a>
            )}
            {server.discord_url && (
              <a href={server.discord_url} className="action-btn discord-btn" target="_blank" rel="noreferrer">
                <FaDiscord size={15} /> {t('website.server_details.discord_btn')}
              </a>
            )}
          </DetailsSidebar>
        </DetailsLayout>

        <CommentsSection>
          <div className="section-title">{t('website.server_details.comments_reviews_title')}</div>

          {/* Comment form — visible only when logged in */}
          {auth.isAuth ? (
            <div className="review-form">
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '12px', fontSize: '13px', color: '#9ca3af'
              }}>
                <span style={{
                  background: auth.user?.color || '#9333EA',
                  color: '#fff', width: '28px', height: '28px',
                  borderRadius: '50%', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontWeight: '700', fontSize: '13px', flexShrink: 0,
                }}>
                  {auth.user?.name?.[0]?.toUpperCase()}
                </span>
                <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{auth.user?.name}</span>
              </div>
              <span className="form-label">{t('website.server_details.your_rating')}</span>
              <StarRow value={rating} onChange={setRating} />
              <textarea
                className="review-textarea"
                placeholder={t('website.server_details.review_placeholder_form')}
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
              />
              <button className="submit-btn" onClick={handleSubmitReview} disabled={commentsLoading || !reviewText.trim()}>
                {commentsLoading ? t('website.server_details.sending') : t('website.server_details.send')}
              </button>
            </div>
          ) : (
            <div className="review-form" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '10px', color: '#6b7280', fontSize: '13px',
            }}>
              <FiLock size={16} />
              <span>{t('website.server_details.login_to_comment')}</span>
              <Link to="/login" style={{
                color: '#a78bfa', fontWeight: '600', textDecoration: 'none',
              }}>
                {t('website.server_details.login_link')}
              </Link>
            </div>
          )}

          <div className="comments-list">
            {comments.map(c => (
              <div className="comment-card" key={c._id}>
                <div className="comment-avatar" style={{ background: c.color }}>{c.author?.[0]}</div>
                <div className="comment-body">
                  <div className="comment-header">
                    <span className="comment-author">{c.author}</span>
                    {c.country && COUNTRIES.find(x => x.code === c.country) && (
                      <img
                        src={COUNTRIES.find(x => x.code === c.country).flag}
                        alt={COUNTRIES.find(x => x.code === c.country).name}
                        title={COUNTRIES.find(x => x.code === c.country).name}
                        style={{ width: 16, height: 11, objectFit: 'cover', borderRadius: 2 }}
                      />
                    )}
                    <CommentStars count={c.stars} />
                    {(() => { const b = getCommentBadge(c.created_at, t); return b ? <span className={`comment-age-badge ${b.cls}`}>{b.label}</span> : null; })()}
                    <span className="comment-date">{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="comment-text">{c.text}</div>
                  <div className="comment-actions">
                    <button className="like-btn" onClick={() => dispatch(likeComment(c))}>
                      <FiThumbsUp size={12} /> {c.likes}
                    </button>
                    <button className="report-btn"><FiFlag size={11} /> {t('website.server_details.report')}</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalComments > 10 && (
            <div className="comments-pagination">
              {Array.from({ length: Math.ceil(totalComments / 10) }, (_, i) => i + 1).map(p => (
                <button key={p} className={commentPage === p ? 'active' : ''}
                  onClick={() => {
                    setCommentPage(p);
                    dispatch(getComments({ page: p, size: 10, serverId: id }));
                  }}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </CommentsSection>
      </WebsiteLayout>
    </HomeWrapper>
  );
};

export default ServerDetails;
